import AssignmentIcon from '@mui/icons-material/Assignment';
import {
    Box,
    Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { LessonPlanSessionModel } from '../../object-actions/types/types';
import { TOTAL_PROGRAM_SESSIONS } from '../../subscriptions/entitlements';
import { ProgramWeek } from '../growth-report/GrowthReportEnqueueButton';

/** Session tabs: green tones. Survey tabs: amber/orange tones. */
const TAB_COLORS = {
    sessionComplete: '#2e7d32',
    sessionIncomplete: '#c8e6c9',
    sessionCurrentRing: '#1565c0',
    surveyComplete: '#ef6c00',
    surveyIncomplete: '#ffe0b2',
    surveyCurrentRing: '#e65100',
} as const;

/** Survey columns are this fraction of a session column width. */
const SURVEY_COLUMN_FR = 0.45;

const isSurveyWeek = (sessionNumber: number): sessionNumber is ProgramWeek =>
    sessionNumber === 4 || sessionNumber === 8 || sessionNumber === 12;

interface LessonPlanSessionNavProps {
    sessions: LessonPlanSessionModel[];
    activeSessionId: number;
    onSelectSession: (sessionId: number, source: 'dropdown' | 'prev_next' | 'session_nav') => void;
    /** Coach / parent / payer: show week 4/8/12 progress-survey tabs. */
    showSurveyTabs?: boolean;
    completedSurveyWeeks?: Partial<Record<ProgramWeek, boolean>>;
    activeSurveyWeek?: ProgramWeek | null;
    onSelectSurvey?: (week: ProgramWeek) => void;
    /** Missing session slot (trial / not yet purchased) — open membership upsell. */
    onUpsellSession?: (sessionNumber: number) => void;
}

const LessonPlanSessionNav: React.FC<LessonPlanSessionNavProps> = ({
    sessions,
    activeSessionId,
    onSelectSession,
    showSurveyTabs = false,
    completedSurveyWeeks = {},
    activeSurveyWeek = null,
    onSelectSurvey,
    onUpsellSession,
}) => {
    const sessionByNumber = useMemo(() => {
        const map = new Map<number, LessonPlanSessionModel>();
        for (const session of sessions) {
            const n = session.session_number;
            if (n != null && !map.has(n)) {
                map.set(n, session);
            }
        }
        return map;
    }, [sessions]);

    const columnTracks: string[] = [];
    for (let i = 0; i < TOTAL_PROGRAM_SESSIONS; i += 1) {
        columnTracks.push('minmax(0, 1fr)');
        const sessionNumber = i + 1;
        if (showSurveyTabs && isSurveyWeek(sessionNumber)) {
            columnTracks.push(`minmax(0, ${SURVEY_COLUMN_FR}fr)`);
        }
    }

    return (
        <Box
            component="nav"
            aria-label="Lesson sessions"
            data-testid="lesson-plan-session-nav"
        >
            <Box
                aria-label="Session and survey progress"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: columnTracks.join(' '),
                    gap: 0.5,
                    alignItems: 'end',
                }}
            >
                {Array.from({ length: TOTAL_PROGRAM_SESSIONS }, (_, index) => {
                    const sessionNumber = index + 1;
                    const session = sessionByNumber.get(sessionNumber);
                    const isPlaceholder = session == null;
                    const isCurrent =
                        !isPlaceholder && session.id === activeSessionId && activeSurveyWeek == null;
                    const isComplete = Boolean(session?.completed_at);
                    const showSurvey = showSurveyTabs && isSurveyWeek(sessionNumber);
                    const surveyComplete = showSurvey ? Boolean(completedSurveyWeeks[sessionNumber]) : false;
                    const surveyCurrent = showSurvey && activeSurveyWeek === sessionNumber;
                    const surveyLabel =
                        sessionNumber === 12 ? 'Final survey' : `Week ${sessionNumber} survey`;

                    const openUpsell = () => onUpsellSession?.(sessionNumber);

                    return (
                        <React.Fragment key={sessionNumber}>
                            <Box
                                component="button"
                                type="button"
                                onClick={() => {
                                    if (isPlaceholder) {
                                        openUpsell();
                                        return;
                                    }
                                    onSelectSession(session.id, 'session_nav');
                                }}
                                aria-label={`Session ${sessionNumber}${isComplete ? ', completed' : ', incomplete'}${isCurrent ? ', current' : ''}${isPlaceholder ? ', membership required' : ''}`}
                                aria-current={isCurrent ? 'step' : undefined}
                                title={
                                    isPlaceholder
                                        ? `Session ${sessionNumber}: available with membership`
                                        : `Session ${sessionNumber}: ${isComplete ? 'complete' : 'incomplete'}`
                                }
                                data-attr={`lesson-plan-session-tab-${sessionNumber}`}
                                data-testid={`lesson-plan-session-tab--${sessionNumber}`}
                                data-complete={isComplete ? 'true' : 'false'}
                                data-placeholder={isPlaceholder ? 'true' : 'false'}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 0.35,
                                    minWidth: 0,
                                    p: 0,
                                    border: 0,
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    opacity: isPlaceholder ? 0.55 : 1,
                                    '&:focus-visible': {
                                        outline: theme => `2px solid ${theme.palette.primary.main}`,
                                        outlineOffset: 2,
                                        borderRadius: 1,
                                    },
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.65rem',
                                        lineHeight: 1,
                                        color: isCurrent ? 'primary.main' : 'text.secondary',
                                        fontWeight: isCurrent ? 700 : 500,
                                    }}
                                >
                                    {sessionNumber}
                                </Typography>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: 8,
                                        borderRadius: 1,
                                        backgroundColor: isComplete
                                            ? TAB_COLORS.sessionComplete
                                            : TAB_COLORS.sessionIncomplete,
                                        boxShadow: isCurrent
                                            ? `inset 0 0 0 2px ${TAB_COLORS.sessionCurrentRing}`
                                            : 'none',
                                        transition: 'transform 160ms ease, background-color 160ms ease',
                                        transform: isCurrent ? 'scaleY(1.25)' : 'none',
                                    }}
                                />
                            </Box>

                            {showSurvey && (
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => {
                                        if (isPlaceholder) {
                                            openUpsell();
                                            return;
                                        }
                                        onSelectSurvey?.(sessionNumber);
                                    }}
                                    aria-label={`${surveyLabel}${surveyComplete ? ', completed' : ', incomplete'}${surveyCurrent ? ', current' : ''}${isPlaceholder ? ', membership required' : ''}`}
                                    aria-current={surveyCurrent ? 'step' : undefined}
                                    title={
                                        isPlaceholder
                                            ? `${surveyLabel}: available with membership`
                                            : `${surveyLabel}: ${surveyComplete ? 'complete' : 'incomplete'}`
                                    }
                                    data-attr={`lesson-plan-survey-tab-${sessionNumber}`}
                                    data-testid={`lesson-plan-survey-tab--${sessionNumber}`}
                                    data-complete={surveyComplete ? 'true' : 'false'}
                                    data-placeholder={isPlaceholder ? 'true' : 'false'}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 0.35,
                                        minWidth: 0,
                                        p: 0,
                                        border: 0,
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        opacity: isPlaceholder ? 0.55 : 1,
                                        '&:focus-visible': {
                                            outline: theme => `2px solid ${theme.palette.warning.main}`,
                                            outlineOffset: 2,
                                            borderRadius: 1,
                                        },
                                    }}
                                >
                                    <AssignmentIcon
                                        sx={{
                                            fontSize: '0.85rem',
                                            color: surveyComplete ? TAB_COLORS.surveyComplete : 'warning.light',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: 8,
                                            borderRadius: 1,
                                            backgroundColor: surveyComplete
                                                ? TAB_COLORS.surveyComplete
                                                : TAB_COLORS.surveyIncomplete,
                                            boxShadow: surveyCurrent
                                                ? `inset 0 0 0 2px ${TAB_COLORS.surveyCurrentRing}`
                                                : 'none',
                                            transition: 'transform 160ms ease, background-color 160ms ease',
                                            transform: surveyCurrent ? 'scaleY(1.25)' : 'none',
                                        }}
                                    />
                                </Box>
                            )}
                        </React.Fragment>
                    );
                })}
            </Box>
        </Box>
    );
};

export default LessonPlanSessionNav;
